import React from 'react';
// import { diffLines, diffWords, diffChars } from 'diff';
import '../styles/DiffViewer.css';

function alignLines(left, right) {
  const leftLines = left.split('\n');
  const rightLines = right.split('\n');
  const result = [];
  let leftIndex = 0;
  let rightIndex = 0;
  let lineNumber = 1;

  while (leftIndex < leftLines.length || rightIndex < rightLines.length) {
    const leftLine = leftLines[leftIndex] || '';
    const rightLine = rightLines[rightIndex] || '';

    // Check if lines are identical after trimming whitespace
    if (leftLine.trim() === rightLine.trim()) {
      result.push({
        number: lineNumber,
        left: { content: leftLine, type: 'unchanged' },
        right: { content: rightLine, type: 'unchanged' }
      });
      leftIndex++;
      rightIndex++;
      lineNumber++;
      continue;
    }

    // Look ahead for better matches
    const nextLeftMatch = rightLines.slice(rightIndex).findIndex(l => l.trim() === leftLine.trim());
    const nextRightMatch = leftLines.slice(leftIndex).findIndex(l => l.trim() === rightLine.trim());

    if (nextLeftMatch === -1 && nextRightMatch === -1) {
      // No exact matches found, treat as modified line
      result.push({
        number: lineNumber,
        left: { content: leftLine, type: 'modified' },
        right: { content: rightLine, type: 'modified' }
      });
      leftIndex++;
      rightIndex++;
      lineNumber++;
    } else if (nextRightMatch === -1 || (nextLeftMatch >= 0 && nextLeftMatch <= nextRightMatch)) {
      // Right has extra content, add blank line on left
      result.push({
        number: null,
        left: { content: '', type: 'blank' },
        right: { content: rightLine, type: 'added' }
      });
      rightIndex++;
    } else {
      // Left has content that's missing from right
      result.push({
        number: lineNumber,
        left: { content: leftLine, type: 'removed' },
        right: { content: '', type: 'blank' }
      });
      leftIndex++;
      lineNumber++;
    }
  }

  return result;
}

function compareChars(text, otherText, isLeft) {
  // Character-by-character comparison with alignment using LCS (Longest Common Subsequence)
  // This prevents highlight propagation after an insertion/deletion.
  if (!text) return null;
  if (!otherText) {
    return <span className={isLeft ? "diff-removed" : "diff-added"}>{text}</span>;
  }

  const a = Array.from(text);
  const b = Array.from(otherText);
  const n = a.length;
  const m = b.length;

  // Build LCS DP table
  const dp = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  // Walk the table to generate aligned spans for the requested side
  const result = [];
  let i = 0;
  let j = 0;
  let key = 0;

  while (i < n || j < m) {
    // If both characters exist and match, emit plain char and advance
    if (i < n && j < m && a[i] === b[j]) {
      result.push(<span key={key++}>{a[i]}</span>);
      i++;
      j++;
      continue;
    }

    // Prefer moving down when dp[i+1][j] >= dp[i][j+1] (a[i] is not matched)
    if (j >= m || (i < n && dp[i + 1][j] >= dp[i][j + 1])) {
      if (i < n) {
        if (isLeft) {
          // Extra character in left (or this side) → removed on the other
          result.push(<span key={key++} className="diff-removed">{a[i]}</span>);
        } else {
          // Extra character in right side compared to left → show as added
          result.push(<span key={key++} className="diff-added">{a[i]}</span>);
        }
        i++;
        continue;
      }
    }

    // Otherwise move right → insertion in right (b[j]) relative to left
    if (j < m) {
      // Moving right means the otherText has an extra character (present in left, missing in right)
      // Do not render anything for this side (both sides should skip showing the counterpart's extra)
      // Left side already skips; here we also skip for right side to avoid showing missing text.
      j++;
      continue;
    }
  }

  return result;
}

export default function DiffViewer({ left, right }) {
  const lines = alignLines(left || '', right || '');
  const hasDifferences = lines.some(line => 
    line.left.type !== 'unchanged' || line.right.type !== 'unchanged'
  );

  const handleDownload = () => {
    let content = 'Text Comparison Results\n';
    content += '======================\n\n';
    
    if (!hasDifferences) {
      content += '✓ No mismatches found - both texts are identical\n';
    } else {
      content += 'Line | Original Text | Modified Text\n';
      content += '-----|--------------|-------------\n';
      
      lines.forEach(line => {
        const leftContent = line.left.content || '';
        const rightContent = line.right.content || '';
        content += `${line.number} | ${leftContent} | ${rightContent}\n`;
      });
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-comparison-result.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const DiffLegend = () => (
    <div className="diff-legend">
      <div className="legend-items">
        <div className="legend-item">
          <span className="legend-color added"></span>
          <span>Added</span>
        </div>
        <div className="legend-item">
          <span className="legend-color removed"></span>
          <span>Removed</span>
        </div>
        <div className="legend-item">
          <span className="legend-color modified"></span>
          <span>Modified Line</span>
        </div>
      </div>
    </div>
  );

  if (!hasDifferences) {
    return (
      <div className="comparison-container">
        <div className="identical-texts">
          <div className="identical-message">
            ✓ No mismatches found - both texts are identical
          </div>
        </div>
        <button className="download-button" onClick={handleDownload}>
          📥 Download Result
        </button>
      </div>
    );
  }

  return (
    <div className="comparison-container">
      <div className="diff-info">
        <DiffLegend />
      </div>
      <div className="diff-viewer">
        <div className="diff-table">
          <div className="diff-header">
            <div className="diff-header-cell line-number">#</div>
            <div className="diff-header-cell">Original</div>
            <div className="diff-header-cell line-number">#</div>
            <div className="diff-header-cell">Modified</div>
          </div>
          <div className="diff-body">
            {lines.map((line, idx) => {
              const isModified = line.left.type !== 'unchanged' || line.right.type !== 'unchanged';
              
              return (
                <div key={idx} className={`diff-row ${isModified ? 'modified' : ''}`}>
                  <div className={`diff-cell line-number ${isModified ? 'modified' : ''}`}>
                    {line.number}
                  </div>
                  <div className={`diff-cell content-cell left-cell ${line.left.type === 'blank' ? 'diff-blank' : ''}`}>
                    {line.left.content ? compareChars(line.left.content, line.right.content, true) : <span className="empty-line">&nbsp;</span>}
                  </div>
                  <div className={`diff-cell line-number ${isModified ? 'modified' : ''}`}>
                    {line.right.type !== 'blank' ? line.number : ''}
                  </div>
                  <div className={`diff-cell content-cell right-cell ${line.right.type === 'blank' ? 'diff-blank' : ''}`}>
                    {line.right.content ? compareChars(line.right.content, line.left.content, false) : <span className="empty-line">&nbsp;</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <button className="download-button" onClick={handleDownload}>
        📥 Download Result
      </button>
    </div>
  );
} 